import React from 'react';
import { SUBTLE } from './tokens';
import { useSoft, RING_ON } from './SurfaceStyleContext';

/**
 * Desktop tab row: equal-width segments in one 2px border, active = black fill.
 * tabs: [{ key, label, count, gated, danger }]. `scroll` lets wide sets (host ops, 7 tabs)
 * scroll horizontally instead of shrinking. Gated tabs render subtle grey but stay clickable
 * (the page decides what a gated tab shows).
 */
export default function SegmentedTabs({ tabs = [], active, onChange, scroll = false, className = '', ariaLabel = 'Sections', testId = 'segmented-tabs' }) {
  const soft = useSoft();
  if (soft) {
    // Soft: 40px pills, the active one black with the rainbow ring, the rest white.
    return (
      <div role="tablist" aria-label={ariaLabel} className={`flex flex-wrap gap-[6px] ${className}`} data-testid={testId} data-soft="1">
        {tabs.map((t) => {
          const on = t.key === active;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={on}
              data-tab={t.key}
              onClick={() => onChange?.(t.key)}
              className="inline-flex items-center gap-[6px] rounded-full font-mono font-bold uppercase transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2"
              style={{
                height: 40, padding: '0 18px', fontSize: 11, letterSpacing: '0.14em', whiteSpace: 'nowrap',
                border: '2px solid transparent', boxSizing: 'border-box',
                background: on ? RING_ON : '#fff',
                color: on ? '#fff' : t.danger ? '#DC2626' : t.gated ? SUBTLE : '#000',
              }}
            >
              {t.label}
              {t.count !== undefined && t.count !== null && t.count !== '' && (
                <span className="rounded-full font-mono font-bold" style={{ padding: '1px 6px', fontSize: 9, letterSpacing: '0.05em', background: on ? '#fff' : '#F2F2F2', color: '#000' }}>{t.count}</span>
              )}
            </button>
          );
        })}
      </div>
    );
  }
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`flex border-2 border-black bg-white ${scroll ? 'overflow-x-auto [&::-webkit-scrollbar]:hidden' : ''} ${className}`}
      style={scroll ? { scrollbarWidth: 'none' } : undefined}
      data-testid={testId}
    >
      {tabs.map((t, i) => {
        const on = t.key === active;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={on}
            data-tab={t.key}
            onClick={() => onChange?.(t.key)}
            className={`flex items-center justify-center gap-[6px] font-mono font-bold uppercase transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-black focus:ring-inset ${scroll ? 'flex-none' : 'min-w-0 flex-1'} ${on ? 'bg-black text-white' : 'bg-white hover:bg-black hover:text-white'}`}
            style={{
              padding: scroll ? '13px 14px' : '13px 6px',
              fontSize: scroll ? 11 : 12,
              letterSpacing: scroll ? '0.03em' : '0.05em',
              borderRight: i < tabs.length - 1 ? '2px solid #000' : 'none',
              color: on ? '#fff' : t.danger ? '#DC2626' : t.gated ? SUBTLE : undefined,
              whiteSpace: 'nowrap',
            }}
          >
            {t.label}
            {t.count !== undefined && t.count !== null && t.count !== '' && (
              <span className="font-mono font-bold" style={{ border: `2px solid ${on ? '#fff' : '#E5E7EB'}`, padding: '0 5px', fontSize: 9, letterSpacing: '0.05em' }}>{t.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
